/**
 * The Charter Board feature (M5 plan C17, spec §15.2-§15.3, §18 flow 4, §19; the career in the Reach only; order
 * right after M4's Wire, 525). With the strategic service up it:
 *
 * - registers the Charter Board as a dashboard section, Intel › Charter (`createCharterSection`; W3 card M1). K
 *   (bindings `charterBoard`) opens the dashboard there and closes it again through the dashboard's own deep links
 *   (`dashboard.json deepLinks`, on since W3 card M4). Station screens keep their Charter tab (a `StationServicePanel`, `charter`). A row's click closes
 *   the dashboard and focuses the galaxy map on that system (on the next unpaused frame if the game is paused then);
 * - registers the five Charter map overlays (claims, states, wars and fronts, law, tolls) in M4's registry.
 *
 * `frame` allocates nothing except on the open and close events (D-014).
 */
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature } from '../../app/feature'
import { logger } from '../../core/logger'
import strategicJson from '../../data/strategic/strategic.json'
import type { StationServiceRegistry } from '../../reach/ports'
import { loadBoardConfig } from '../../ui/charter/boardModel'
import { createBoardData, createCharterSection, createCharterStationPanel } from '../../ui/charter/host'
import { installCharterOverlays, type CharterOverlays } from '../../ui/charter/overlays'
import { loadDashboardConfig } from '../../ui/dashboard/config'
import { openDashboard, registerDashSection } from '../../ui/dashboard/registry'
import mapsFeature from '../maps/feature'

/** Right after the Wire (525), before the Charter HUD (530). */
const ORDER = FEATURE_ORDER.wire + 1

/** Registries the Charter tab already went into (the registry has no removal). */
const registered = new WeakSet<StationServiceRegistry>()

export interface CharterBoardFeature extends GameFeature {
  readonly active: boolean
  /** Opens the dashboard at Intel › Charter (the corporation registration screen, the debug API). */
  openBoard(): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export function createCharterBoardFeature(): CharterBoardFeature {
  let overlays: CharterOverlays | null = null
  let active = false
  let paused = false
  let pendingMap: string | null = null
  const offs: (() => void)[] = []

  function selectSystem(ctx: GameContext, systemId: string): void {
    if (!paused && ctx.ui.modal === 0) {
      logger.debug('Charter Board: focusing the map', { systemId })
      mapsFeature.open(ctx, systemId)
    } else pendingMap = systemId
  }

  const feature: CharterBoardFeature = {
    name: 'charterBoard',
    order: ORDER,
    get active(): boolean {
      return active
    },

    init(ctx: GameContext): void {
      const client = ctx.services.maybe('strategic')
      const reach = ctx.reach
      if (client === undefined || reach === null) return
      const config = loadBoardConfig()
      const data = createBoardData(client, reach.galaxy, strategicJson.clock.dayS)
      active = true
      overlays = installCharterOverlays(client, config.overlays)
      offs.push(registerDashSection(createCharterSection({ data, config, onSelect: (id) => selectSystem(ctx, id) })))
      const services = reach.services
      if (!registered.has(services)) {
        registered.add(services)
        services.register(
          createCharterStationPanel({
            data,
            config,
            onSelect: (id) => selectSystem(ctx, id),
          }),
        )
      }
    },

    openBoard(): void {
      if (!active) return
      openDashboard(loadDashboardConfig().deepLinkTargets.charterBoard)
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      if (!active) return
      paused = f.paused
      if (f.paused) return
      if (pendingMap !== null && ctx.ui.modal === 0) {
        const id = pendingMap
        pendingMap = null
        mapsFeature.open(ctx, id)
      }
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
      overlays?.dispose()
      overlays = null
      active = false
      pendingMap = null
    },
  }
  return feature
}

const feature: CharterBoardFeature = createCharterBoardFeature()

export default feature
